import {
  AuditRecordSchema,
  MeSchema,
  N8nTestResultSchema,
  OutboxStatusSchema,
  SettingSchema,
  StaffUserSchema,
  type CreateUserRequest,
  type UpdateUserRequest,
} from '@deli/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as z from 'zod';
import { getJson, isUnauthorized, sendJson } from './client.js';

// ── Session ──────────────────────────────────────────────────────────────────────────────

/** The signed-in user, or null when signed out (a 401 is an answer here, not an error). */
export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn: async ({ signal }) => {
      try {
        return await getJson('/api/v1/auth/me', MeSchema, { signal });
      } catch (error) {
        if (isUnauthorized(error)) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      sendJson('POST', '/api/v1/auth/login', body, MeSchema),
    onSuccess: (me) => client.setQueryData(['me'], me),
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => sendJson('POST', '/api/v1/auth/logout', undefined, null),
    onSuccess: () => {
      client.clear();
      client.setQueryData(['me'], null);
    },
  });
}

// ── Users ────────────────────────────────────────────────────────────────────────────────

export const useUsers = () =>
  useQuery({
    queryKey: ['users'],
    queryFn: ({ signal }) => getJson('/api/v1/users', z.array(StaffUserSchema), { signal }),
  });

function useUsersMutation<T>(fn: (input: T) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ['users'] }),
  });
}

export const useCreateUser = () =>
  useUsersMutation((body: CreateUserRequest) =>
    sendJson('POST', '/api/v1/users', body, StaffUserSchema),
  );

export const useUpdateUser = () =>
  useUsersMutation(({ id, ...body }: UpdateUserRequest & { id: string }) =>
    sendJson('PATCH', `/api/v1/users/${id}`, body, StaffUserSchema),
  );

export const useSetPassword = () =>
  useUsersMutation(({ id, password }: { id: string; password: string }) =>
    sendJson('PUT', `/api/v1/users/${id}/password`, { password }, null),
  );

// ── Settings and n8n ─────────────────────────────────────────────────────────────────────

export const useSettings = () =>
  useQuery({
    queryKey: ['settings'],
    queryFn: ({ signal }) => getJson('/api/v1/settings', z.array(SettingSchema), { signal }),
  });

export function useSaveSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: number | string | null }) =>
      sendJson('PUT', `/api/v1/settings/${key}`, { value }, SettingSchema),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings'] });
      void client.invalidateQueries({ queryKey: ['n8n'] });
      void client.invalidateQueries({ queryKey: ['store'] });
    },
  });
}

export const useN8nStatus = () =>
  useQuery({
    queryKey: ['n8n', 'status'],
    queryFn: ({ signal }) =>
      getJson('/api/v1/integrations/n8n/status', OutboxStatusSchema, { signal }),
  });

export const useStoreStatus = () =>
  useQuery({
    queryKey: ['store', 'status'],
    queryFn: ({ signal }) =>
      getJson('/api/v1/integrations/store/status', OutboxStatusSchema, { signal }),
  });

export function useN8nTest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      sendJson('POST', '/api/v1/integrations/n8n/test', undefined, N8nTestResultSchema),
    onSettled: () => client.invalidateQueries({ queryKey: ['n8n'] }),
  });
}

export function useDiscardBacklog() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      sendJson(
        'POST',
        '/api/v1/integrations/n8n/outbox/discard',
        undefined,
        z.object({ discarded: z.number() }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['n8n'] }),
  });
}

// ── Audit ────────────────────────────────────────────────────────────────────────────────

export interface AuditFilter {
  entity?: string;
  entityId?: string;
}

export const PAGE_SIZE = 50;

export function fetchAuditPage(filter: AuditFilter, beforeId?: number, signal?: AbortSignal) {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (filter.entity) params.set('entity', filter.entity);
  if (filter.entityId) params.set('entityId', filter.entityId);
  if (beforeId) params.set('beforeId', String(beforeId));
  return getJson(`/api/v1/audit?${params}`, z.array(AuditRecordSchema), { signal });
}
